import type { SearchInputProps } from '@meterup/atto';
import { SearchInput, Shortcut, shortcutsVisible, SpecialKey, useUsersOS } from '@meterup/atto';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDebouncedCallback } from 'use-debounce';

import {
  useKeyBoundSearchInputRegistry,
  useSearchParamsState,
} from '../providers/SearchParamsStateProvider';

function getShortcutForIndex(
  index: number,
  isMac: boolean,
): [hotkey: string, shortcutKeys: (SpecialKey | string)[]] {
  const mod = isMac ? 'meta' : 'ctrl';
  const modKey = isMac ? SpecialKey.Meta : SpecialKey.Ctrl;
  if (index === 0) return [`${mod}+f`, [modKey, 'F']];
  return [`${mod}+g`, [modKey, 'G']]; // index 1+ share ⌘G
}

type KeyBoundSearchInputProps = Omit<SearchInputProps, 'inputRef'> & {
  id?: string;
  debounceMs?: number;
  minSearchLength?: number;
  searchParamStateKey: string;
  shortcutPriority?: number;
};

export interface KeyBoundSearchInputRef {
  focus: () => void;
}

export const KeyBoundSearchInput = forwardRef<KeyBoundSearchInputRef, KeyBoundSearchInputProps>(
  (
    {
      debounceMs = 300, // Increased from 250ms for better performance
      id,
      minSearchLength = 0,
      searchParamStateKey,
      shortcutPriority,
      ...remaining
    },
    ref,
  ) => {
    const platform = useUsersOS();
    const registry = useKeyBoundSearchInputRegistry();
    const inputRef = useRef<HTMLInputElement>(null);
    const [querySearch, setQuerySearch] = useSearchParamsState<string>(searchParamStateKey, '');
    const [localQuerySearch, setLocalQuerySearch] = useState(querySearch ?? '');
    const [isFocused, setIsFocused] = useState(false);

    const registryKey =
      shortcutPriority !== undefined
        ? `${String(shortcutPriority).padStart(2, '0')}-${searchParamStateKey}`
        : searchParamStateKey;

    const register = registry?.register;
    const unregister = registry?.unregister;

    // Use register/unregister in deps (stable) to avoid loop when registry keys change
    useEffect(() => {
      if (!register || !unregister) return () => {};
      register(registryKey);
      return () => {
        unregister(registryKey);
      };
    }, [register, unregister, registryKey]);

    const isMac = platform === 'mac';
    const shortcutIndex = registry?.getIndex(registryKey) ?? 0;
    const [hotkey, shortcutKeys] = getShortcutForIndex(shortcutIndex, isMac);
    const debouncedQuerySearch = useDebouncedCallback((val: string) => {
      // Only update if search meets minimum length or is empty
      if (val.length === 0 || val.length >= minSearchLength) {
        setQuerySearch(val);
      }
    }, debounceMs);

    // update local state when query changes
    // edge case: the SearchParamsState does not initialize state when this component mounts on back button navigation
    useEffect(() => {
      if (querySearch != null) {
        setLocalQuerySearch(querySearch);
      }
    }, [querySearch]);

    const handleQuerySearchChange = useCallback(
      (val: string) => {
        setLocalQuerySearch(val);
        debouncedQuerySearch(val);
      },
      [setLocalQuerySearch, debouncedQuerySearch],
    );

    const handleFocus = useCallback(() => {
      setIsFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
      setIsFocused(false);
    }, []);

    // Expose focus method to parent component
    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        },
      }),
      [],
    );

    useHotkeys(
      hotkey,
      () => {
        if (inputRef?.current && !isFocused) {
          inputRef.current.focus();
        }
      },
      { preventDefault: !isFocused },
    );

    return (
      <SearchInput
        id={id}
        placeholder="..."
        data-testid="searchInput"
        inputRef={inputRef}
        onChange={handleQuerySearchChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        value={localQuerySearch}
        size="small"
        suffix={!isFocused && shortcutsVisible ? <Shortcut keys={shortcutKeys} /> : undefined}
        {...remaining}
      />
    );
  },
);
